/**
 * Capability derivation for NeoTIM
 * Pure TypeScript module - no React, no hooks, no side effects
 * 
 * This module derives UI capabilities from raw permission strings.
 * 
 * IMPORTANT: Capabilities are UI-only.
 * - Use can() / canAny() / canAll() for authorization decisions
 * - Use capabilities.has() only for deciding which components to MOUNT
 * - Never use capabilities in route beforeLoad guards - use requirePermission() instead
 */

import type { CapabilityId, PermissionContext, UserType } from './types'

/**
 * Derive capabilities from permission strings and user context
 */
export function deriveCapabilities(
  permissions: readonly string[],
  context: PermissionContext
): Set<CapabilityId> {
  const caps = new Set<CapabilityId>()

  // Helper to check if any permission matches a pattern
  const hasPermissionMatching = (pattern: (perm: string) => boolean): boolean =>
    Array.from(permissions).some(pattern)

  // Helper to check if permission starts with a prefix
  const hasPermissionStartingWith = (prefix: string): boolean =>
    hasPermissionMatching((p) => p.startsWith(prefix))

  // admin: has wildcard permission
  if (hasPermissionMatching((p) => p === '*')) {
    caps.add('admin')
  }

  // hr_global: has rh write permissions with filiale:own but no scope:managed
  // (DRH level: manages entire filiale)
  const hasRhFilialeOwn = hasPermissionStartingWith('rh.') && hasPermissionMatching((p) =>
    p.includes(':filiale:own') && !p.includes(':scope:')
  )
  if (hasRhFilialeOwn) {
    caps.add('hr_global')
  }

  // hr_dept: has rh validate with owned scope AND is dept head
  const hasHrDeptPermission = hasPermissionMatching(
    (p) =>
      p.startsWith('rh.') &&
      p.includes(':filiale:own') &&
      p.includes(':scope:managed')
  )
  if (hasHrDeptPermission && context.is_dept_head) {
    caps.add('hr_dept')
  }

  // hr_team: has rh validate with owned scope, NOT dept head, IS manager
  if (hasHrDeptPermission && !context.is_dept_head && context.is_manager) {
    caps.add('hr_team')
  }

  // hr_self: has self-scoped permissions but not global/dept/team
  const hasHrSelf = hasPermissionMatching((p) => p.includes(':scope:self'))
  if (hasHrSelf && !caps.has('hr_global') && !caps.has('hr_dept') && !caps.has('hr_team')) {
    caps.add('hr_self')
  }

  // hr_multifiliale: has rh permissions with :filiale:* (all filiales)
  const hasHrMultifiliale = hasPermissionMatching(
    (p) => p.startsWith('rh.') && p.includes(':filiale:*')
  )
  if (hasHrMultifiliale) {
    caps.add('hr_multifiliale')
  }

  // audit: has view-only access to rh, no create/edit/delete/validate
  const hasRhViewOwn = hasPermissionStartingWith('rh.') &&
    hasPermissionMatching((p) => p.includes(':filiale:own'))
  const hasRhWrite = hasPermissionMatching(
    (p) =>
      p.startsWith('rh.') &&
      (p.includes('.create') || p.includes('.edit') || p.includes('.delete') || p.includes('.validate'))
  )
  if (hasRhViewOwn && !hasRhWrite) {
    caps.add('audit')
  }

  // security_admin: has security.users.view
  if (hasPermissionStartingWith('security.')) {
    caps.add('security_admin')
  }

  return caps
}

/**
 * Determine user type from capabilities
 */
export function determineUserType(capabilities: Set<CapabilityId>): UserType {
  if (capabilities.has('admin')) {
    return 'admin'
  }

  if (capabilities.has('hr_global') || capabilities.has('hr_multifiliale')) {
    return 'hr-global'
  }

  if (capabilities.has('hr_dept') || capabilities.has('hr_team')) {
    return 'manager'
  }

  // Default to employee for hr_self or no capabilities
  return 'employee'
}

/**
 * Get dashboard route based on capabilities
 */
export function getDashboardRoute(capabilities: Set<CapabilityId>): string {
  if (capabilities.has('admin')) {
    return '/admin'
  }

  if (capabilities.has('hr_global') || capabilities.has('hr_multifiliale')) {
    return '/rh'
  }

  if (capabilities.has('hr_dept') || capabilities.has('hr_team')) {
    return '/rh/team'
  }

  if (capabilities.has('security_admin')) {
    return '/security'
  }

  // Default employee dashboard
  return '/my'
}
